import { useMemo } from 'react';

import type { VariantProp } from '../common/colors';
import type { CSS } from '../stitches.config';
import { useVariant } from '../common/variant';
import { darkThemeSelector, shadows } from '../stitches.config';

/**
 * Returns CSS styles for variant-aware shadows. Shadows are optimized for each
 * surface variant (alternative, attention, brand, etc.) with light/dark mode awareness.
 * The stroke/border portion of the shadow is tinted to match the surface.
 */
export function getVariantShadowStyles(variant: VariantProp, thick?: boolean): CSS {
  const cap = variant.charAt(0).toUpperCase() + variant.slice(1);
  const lightKey = `overlay${thick ? 'Thick' : ''}${cap}Light` as keyof typeof shadows;
  const darkKey = `overlay${thick ? 'Thick' : ''}${cap}Dark` as keyof typeof shadows;

  const lightShadow = shadows[lightKey];
  const darkShadow = shadows[darkKey];

  if (!lightShadow || !darkShadow) {
    // Fallback to neutral if variant shadow doesn't exist
    const fallbackLight =
      shadows[`overlay${thick ? 'Thick' : ''}NeutralLight` as keyof typeof shadows];
    const fallbackDark =
      shadows[`overlay${thick ? 'Thick' : ''}NeutralDark` as keyof typeof shadows];
    return {
      boxShadow: fallbackLight ?? shadows.overlayLight,
      [darkThemeSelector]: { boxShadow: fallbackDark ?? shadows.overlayDark },
    };
  }

  return {
    boxShadow: lightShadow,
    [darkThemeSelector]: { boxShadow: darkShadow },
  };
}

export interface UseVariantAwareShadowOptions {
  /**
   * Override the variant. When not provided, uses variant from nearest VariantProvider.
   */
  variant?: VariantProp;
  /**
   * Whether to use thick shadow.
   */
  thick?: boolean;
}

/**
 * Hook that returns variant-aware shadow styles. Uses VariantProvider context when
 * variant is not explicitly provided, making overlays (dropdowns, popovers, tooltips)
 * automatically match the surface they float above.
 *
 * @example
 *   // Inside a branded section - shadow border will match brand stroke
 *   <VariantProvider value="brand">
 *     <DropdownMenu css={useVariantAwareShadow()} />
 *   </VariantProvider>
 *
 *   // Explicit variant
 *   <div css={useVariantAwareShadow({ variant: 'positive', type: 'overlayThick' })} />
 */
export function useVariantAwareShadow(options?: UseVariantAwareShadowOptions): CSS {
  const contextVariant = useVariant(options?.variant);
  const variant = options?.variant ?? contextVariant;
  const thick = options?.thick ?? false;

  return useMemo(() => getVariantShadowStyles(variant, thick), [variant, thick]);
}
